import { createMuiTheme } from "@material-ui/core";
import colors from "Styles/colors";

const mainThemeColors = {
  primary: {
    main: colors.tangaroa,
    dark: colors.darkTangaroa,
    light: colors.lightTangaroa,
    contrastText: colors.white,
  },
  secondary: {
    main: colors.vermilion,
    dark: colors.darkVermilion,
    light: colors.lightVermilion,
    contrastText: colors.white,
  },
};

const theme = createMuiTheme({
  palette: {
    ...mainThemeColors,
    grey: {
      50: colors.whiteGray,
      100: colors.grayLight,
      200: colors.grayMedLight,
      300: colors.grayMed,
      500: colors.grayDark,
    },
    error: {
      main: colors.red,
      contrastText: colors.white,
    },
    background: {
      default: colors.white,
    },
    text: {
      primary: colors.black,
    },
    action: {
      selected: colors.grayMedLight,
      hover: colors.grayLight,
    },
    success: {
      main: colors.green,
      dark: colors.fernGreen,
    },
    warning: {
      main: colors.tahitiGold,
    },
  },
  typography: {
    fontFamily: "graphik, 'Helvetica Neue', Arial, sans-serif",
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 700,
    h1: {
      // super large black
      color: colors.black,
      fontSize: "34px",
      fontWeight: 400,
      marginBottom: "10px",
    },
    h2: {
      // large black bold
      color: colors.black,
      fontSize: "28px",
      fontWeight: 600,
      marginBottom: "10px",
    },
    h6: {
      // normal case but black
      color: colors.black,
      fontSize: "16px",
      fontWeight: 600,
      margin: "24px 0",
    },
    h3: {
      // upper case gray background
      color: colors.black,
      fontSize: "16px",
      fontWeight: 600,
      textTransform: "uppercase",
      backgroundColor: colors.grayMedLight,
      paddingTop: 8,
      paddingBottom: 8,
      paddingLeft: 22,
      marginBottom: 14,
    },
    h4: {
      // uppper cased gray
      color: colors.grayDark,
      fontSize: "16px",
      fontWeight: "normal",
      textTransform: "uppercase",
      marginBottom: "10px",
    },
    h5: {
      // normal case gray
      color: colors.grayDark,
      fontSize: "16px",
      fontWeight: "normal",
      marginBottom: 10,
    },
    body1: {
      color: colors.black,
      fontSize: 16,
    },
    body2: {
      color: colors.black,
      fontSize: 16,
      marginBottom: 18,
    },
  },
  overrides: {
    MuiCheckbox: {
      colorPrimary: { color: colors.grayMedLight },
    },
    MuiChip: {
      root: {
        fontWeight: 500,
      },
    },
    MuiOutlinedInput: {
      root: {
        "&:hover $notchedOutline": {
          borderColor: colors.grayMed,
        },
        "&$focused $notchedOutline": {
          borderWidth: 1,
        },
      },
      input: {
        padding: "14px 42px 13px 18px",
      },
      multiline: {
        padding: "14px 18px 13px 18px",
      },
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: colors.whiteBlue,
        border: `1px solid ${colors.black}`,
        borderRadius: "2px",
        boxShadow: "none",
      },
    },
    MuiSelect: {
      outlined: {
        minWidth: "160px",
        padding: "14px 42px 13px 18px",
        paddingLeft: "18px",
        paddingRight: "42px",
        fontSize: "15px",
        fontWeight: 500,
        borderColor: colors.grayMed,
        color: colors.black,
      },
      icon: {
        marginRight: "10px",
      },
      root: {
        zIndex: 1,
      },
    },
    MuiInputLabel: {
      root: {
        fontSize: "11px",
        fontWeight: 600,
        color: colors.black,
      },
    },
    MuiPaper: {
      root: {
        padding: "15px",
        marginBottom: "10px",
      },
    },
    MuiButton: {
      root: {
        borderRadius: 2,
        textTransform: "capitalize",
      },
      text: {
        fontWeight: 600,
        textDecoration: "underline",
        "&:hover": {
          color: mainThemeColors.primary.main,
          textDecoration: "underline",
          backgroundColor: "transparent",
        },
      },
      textPrimary: {
        "&:hover": {
          color: mainThemeColors.primary.main,
          backgroundColor: "transparent",
        },
      },
      contained: {
        paddingTop: 8,
        paddingBottom: 8,
        "box-shadow": "none",
      },
    },
    MuiIconButton: {
      root: {
        "&:hover": {
          color: mainThemeColors.primary.main,
          backgroundColor: "transparent",
        },
      },
    },
    MuiLink: {
      root: {
        fontWeight: 600,
        "&:hover": {
          color: mainThemeColors.primary.main,
        },
      },
    },
    MuiMenuItem: {
      root: {
        "&:hover": { backgroundColor: colors.grayMedLight },
      },
    },
    MuiTableHead: {
      root: {
        border: `2px solid ${colors.grayMed}`,
        borderBottomColor: mainThemeColors.primary.main,
      },
    },
    MuiTableBody: {
      root: {
        border: `2px solid ${colors.grayMed}`,
        borderTop: "none",
      },
    },
    MuiTableRow: {
      hover: {
        "&$hover:hover": {
          backgroundColor: colors.grayMedLight, // TODO: we may want to remove this
          borderLeft: `2px solid ${mainThemeColors.secondary.main}`
        },
      },
    },
    MuiTableCell: {
      root: {
        fontSize: 14,
        lineHeight: 1.43,
        borderColor: colors.grayMed,
        "&:last-child": {
          borderRight: `2px solid ${colors.grayMed}`,
        },
        "&:first-of-type": {
          paddingLeft: 32,
        },
      },
      head: {
        color: colors.black,
        borderBottom: "none",
        fontWeight: 600,
        paddingTop: 16,
        paddingBottom: 16,
        position: "relative",
      },
      footer: {
        "&:last-child": {
          borderRight: "none",
        },
      },
    },
    MuiTablePagination: {
      root: {
        border: "none",
        color: colors.black,
      },
      spacer: {
        display: "none",
      },
      toolbar: {
        padding: "22px 0 0 0",
        display: "flex",
        justifyContent: "center",
        height: "auto",
      },
    },
  },
});

export default theme;
